import { Modal } from "components/views/modal/Modal"
import React, { useContext, useEffect, useState } from "react"
import L10n from "lib/carrot-l10n"
import { createErrorBoundary } from "utils/createErrorBoundary"
import { LeftBackArrowImage } from "../../fle/Molecules"
import { useSelector } from "react-redux"
import { getNeedsBenefitEnrollment, getConnectInsuranceInProgress } from "../../../../derivedSelectors"
import { ArrowLink } from "../../atoms/links/ArrowLink"
import { Body } from "../../atoms/Bodies"
import { FormSingleSelect } from "../../atoms/forms/FormSingleSelect"
import ProcessTracker from "../ProgressTracker"
import UnhandledEnrollmentErrorView from "./UnhandledEnrollmentErrorView"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { useFormContext } from "react-hook-form"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

export type BenefitEnrollmentFlowModalContextProps = {
  onExit: () => void
  needsEnrollment: any
  setNeedsEnrollment: (boolean) => void
  connectInsuranceInProgress: boolean
  setConnectInsuranceInProgress: (boolean) => void
}

export const BenefitEnrollmentFlowModalContext = React.createContext<BenefitEnrollmentFlowModalContextProps>(null)

export const useBenefitEnrollmentFlow = () => useContext(BenefitEnrollmentFlowModalContext)

//@ts-ignore
const BenefitEnrollmentErrorBoundary = createErrorBoundary(UnhandledEnrollmentErrorView)

const Required = "Required"

const BenefitEnrollmentFlowModal = ({ onExit }) => {
  const { step } = useStateMachine()
  // There's currently no easy way to simply get an answer to "Do I have a valid enrollment?". This is mostly due to
  // the lack of an end date on enrollments in the database. The workaround for now is to use the enrollment fields
  // we're capturing in the wizard to make the determination.
  //
  // We snapshot the current value of needsEnrollment for usage later in the wizard to be sure we don't update the
  // benefit enrollments multiple times. We cannot just use the getNeedsBenefitEnrollment selector later because
  // at the point it's needed, getNeedsBenefitEnrollment will return false even when we haven't actually updated the
  // benefit enrollment.
  const [needsEnrollment, setNeedsEnrollment] = useState(useSelector(getNeedsBenefitEnrollment))
  const [connectInsuranceInProgress, setConnectInsuranceInProgress] = useState(
    useSelector(getConnectInsuranceInProgress)
  )

  const context = {
    onExit,
    needsEnrollment,
    setNeedsEnrollment,
    connectInsuranceInProgress,
    setConnectInsuranceInProgress
  }

  return (
    <Modal titleText={l`Benefit enrollment`} onExit={onExit} underlayClickExits={false}>
      <div className="w-100 ph45 pb45">
        <BenefitEnrollmentFlowModalContext.Provider value={context}>
          <ProcessTracker numSteps="2">
            <BenefitEnrollmentErrorBoundary>{React.createElement(step.view)}</BenefitEnrollmentErrorBoundary>
          </ProcessTracker>
        </BenefitEnrollmentFlowModalContext.Provider>
      </div>
    </Modal>
  )
}

const BenefitEnrollmentFlowModalBackButton = () => {
  const { gotoPreviousStep } = useStateMachine()

  return (
    <div
      className="pointer bg-carrot-orange-01 br-100 flex items-center justify-center"
      style={{
        height: "2rem",
        width: "2rem"
      }}
      onClick={gotoPreviousStep}
    >
      <LeftBackArrowImage />
    </div>
  )
}

const BenefitEnrollmentModalContinueButton = ({ onClick }) => {
  return (
    <div className="fr">
      <ArrowLink onClickOnly to="#" onClick={onClick} weight="fw5" TextComponent={Body}>
        Continue
      </ArrowLink>
    </div>
  )
}

const BenefitEnrollmentModalFormSelect = ({ defaultValue = null, ...otherProps }) => {
  const { setValue } = useFormContext()
  const value = defaultValue !== null ? String(defaultValue) : null
  useEffect(() => {
    setValue(
      otherProps.name,
      otherProps.options.find(({ id }) => id === value)
    )
  }, [])
  return <FormSingleSelect rules={{ required: Required }} {...otherProps} />
}

export {
  BenefitEnrollmentFlowModal,
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
}
